import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SiteListItem } from './SiteListItem';
import { SiteListHeader } from './SiteListHeader';
import { SiteSearchEmptyState } from './SiteSearchEmptyState';
import { ForNeighborsError } from './classifyForNeighborsError';
import ErrorBoundary from '../errorBoundaries/ErrorBoundary';
import PantrySpinner from '../pantry/PantrySpinner';
import { useTranslation } from '../../providers/TranslationProvider';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import { Site } from '../../types/Site';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.lightGrayBackground,
  },
  listContent: {
    gap: 12,
    padding: 14,
  },
  errorBanner: {
    backgroundColor: Colors.errorBackground,
    padding: 16,
  },
  errorTitle: {
    color: Colors.errorText,
    fontWeight: '600',
    marginBottom: 4,
  },
  errorSubtitle: {
    color: Colors.errorText,
  },
  errorRetryLink: {
    color: Colors.primaryColor,
    textDecorationLine: 'underline',
    marginTop: 12,
  },
  truncatedBanner: {
    backgroundColor: Colors.lightGrayBackground,
    padding: 12,
  },
  truncatedText: {
    color: Colors.grayText,
    textAlign: 'center',
  },
});

export interface SiteListProps {
  sites: Site[];
  onSelectSite: (site: Site) => void;
  loading?: boolean;
  error?: ForNeighborsError | null;
  resultsTruncated?: boolean;
  query?: string;
  onRetry?: () => void;
}

export function SiteList({
  sites,
  onSelectSite,
  loading = false,
  error = null,
  resultsTruncated = false,
  query = '',
  onRetry,
}: SiteListProps) {
  const { t, translation } = useTranslation();

  if (loading) {
    return (
      <View style={styles.container} testID="site-list-loading">
        <PantrySpinner translation={translation} showHeader={false} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container} testID="site-list-error">
        <View style={styles.errorBanner}>
          <AppText style={styles.errorTitle}>{t('search_error_unable_to_display')}</AppText>
          <AppText style={styles.errorSubtitle}>{t('search_error_occurred')}</AppText>
          {onRetry && (
            <AppText style={styles.errorRetryLink} onPress={onRetry} testID="site-list-retry">
              {t('site_map_retry')}
            </AppText>
          )}
        </View>
      </View>
    );
  }

  // Only shown for a search that returned zero results -- an empty explore-mode list (no query)
  // falls through to the plain empty ScrollView below instead of this search-flavored copy.
  if (query && sites.length === 0) {
    return (
      <View style={styles.container} testID="site-list-empty">
        <SiteSearchEmptyState query={query} />
      </View>
    );
  }

  return (
    <View style={styles.container} testID="site-list">
      <SiteListHeader resultCount={sites.length} />
      {resultsTruncated && (
        <View style={styles.truncatedBanner} testID="site-list-truncated-banner">
          <AppText style={styles.truncatedText}>{t('search_results_truncated')}</AppText>
        </View>
      )}
      <ScrollView contentContainerStyle={styles.listContent}>
        {/* Per-item boundary: this endpoint is still stabilizing, so one site with an unexpected
            shape shouldn't hide every other (valid) site in the list. */}
        {sites.map((site) => (
          <ErrorBoundary key={site.siteId} fallback={null}>
            <SiteListItem site={site} onSelect={() => onSelectSite(site)} />
          </ErrorBoundary>
        ))}
      </ScrollView>
    </View>
  );
}
